// Industry schema definitions — single source of truth.
// Each industry defines tabs and fields for the structured client content editor.
//
// Field types:
//   text        — single line text input
//   textarea    — multi-line text input
//   image       — single image upload
//   url         — URL input
//   hours       — per-day open/close time table (Mon–Sun)
//   list        — repeatable items, each with sub-fields (subFields array)
//   multi-image — multiple image uploads (gallery)

export const INDUSTRIES = {
  restaurant: {
    label: 'Restaurant',
    icon: '🍽️',
    tabs: [
      {
        id: 'business',
        label: 'Business Info',
        fields: [
          { key: 'business_name',  label: 'Business Name',  type: 'text',     placeholder: "Joe's Kitchen" },
          { key: 'tagline',        label: 'Tagline',        type: 'text',     placeholder: 'Best food in town' },
          { key: 'about',          label: 'About',          type: 'textarea', placeholder: 'Tell your story...' },
          { key: 'cuisine_type',   label: 'Cuisine Type',   type: 'text',     placeholder: 'Italian, Mexican, etc.' },
          { key: 'logo',           label: 'Logo',           type: 'image' },
          { key: 'hero_image',     label: 'Hero Image',     type: 'image' },
          { key: 'order_link',     label: 'Order Online Link', type: 'url',  placeholder: 'https://ubereats.com/...' },
        ],
      },
      {
        id: 'menu',
        label: 'Menu',
        fields: [
          {
            key: 'menu',
            label: 'Menu Items',
            type: 'list',
            variant: 'menu',
            addLabel: 'Add Menu Item',
            subFields: [
              { key: 'category',    label: 'Category',    type: 'text',     placeholder: 'Starters, Mains, Desserts...' },
              { key: 'name',        label: 'Item Name',   type: 'text',     placeholder: 'Margherita Pizza' },
              { key: 'description', label: 'Description', type: 'textarea', placeholder: 'Tomato, mozzarella, basil' },
              { key: 'price',       label: 'Price',       type: 'text',     placeholder: '£12.99' },
              { key: 'image',       label: 'Photo',       type: 'image' },
            ],
          },
          {
            key: 'specials',
            label: "Today's Specials",
            type: 'list',
            addLabel: 'Add Special',
            subFields: [
              { key: 'name',        label: 'Name',        type: 'text',     placeholder: 'Weekend Brunch Special' },
              { key: 'description', label: 'Description', type: 'textarea', placeholder: 'Description...' },
              { key: 'price',       label: 'Price',       type: 'text',     placeholder: '£15.00' },
              { key: 'image',       label: 'Photo',       type: 'image' },
            ],
          },
        ],
      },
      { id: 'hours',   label: 'Hours',   fields: [{ key: 'hours',   label: 'Opening Hours', type: 'hours' }, { key: 'holiday_hours', label: 'Holiday hours note', type: 'text', placeholder: 'e.g. Closed Christmas Day and Jan 1' }] },
      { id: 'gallery', label: 'Gallery', fields: [{ key: 'gallery', label: 'Gallery',        type: 'multi-image' }] },
      {
        id: 'contact',
        label: 'Contact',
        fields: [
          { key: 'phone',     label: 'Phone',     type: 'text', placeholder: '+44 7700 900000' },
          { key: 'email',     label: 'Email',     type: 'text', placeholder: 'hello@restaurant.com' },
          { key: 'address',   label: 'Address',   type: 'textarea', placeholder: '123 High Street, London' },
          { key: 'instagram', label: 'Instagram', type: 'url',  placeholder: 'https://instagram.com/...' },
          { key: 'facebook',  label: 'Facebook',  type: 'url',  placeholder: 'https://facebook.com/...' },
          { key: 'tiktok',    label: 'TikTok',    type: 'url',  placeholder: 'https://tiktok.com/...' },
          { key: 'maps_link', label: 'Google Maps Link', type: 'url', placeholder: 'https://maps.google.com/...' },
        ],
      },
      {
        id: 'seo',
        label: 'SEO',
        fields: [
          { key: 'seo_title',       label: 'Page Title',       type: 'text',     placeholder: "Joe's Kitchen — Best Pizza in London" },
          { key: 'seo_description', label: 'Meta Description', type: 'textarea', placeholder: 'Up to 160 characters...' },
        ],
      },
    ],
  },

  food_truck: {
    label: 'Food Truck',
    icon: '🚚',
    tabs: [
      {
        id: 'business',
        label: 'Business Info',
        fields: [
          { key: 'business_name', label: 'Business Name',  type: 'text',     placeholder: "Marco's Food Truck" },
          { key: 'tagline',       label: 'Tagline',        type: 'text',     placeholder: 'Street food done right' },
          { key: 'about',         label: 'About',          type: 'textarea', placeholder: 'Our story...' },
          { key: 'cuisine_type',  label: 'Cuisine Type',   type: 'text',     placeholder: 'Tacos, BBQ, Asian fusion...' },
          { key: 'opening_days',  label: 'Opening Days',   type: 'text',     placeholder: 'Friday – Sunday' },
          { key: 'opening_hours', label: 'Opening Hours',  type: 'text',     placeholder: '12:00 – 22:00' },
          { key: 'logo',          label: 'Logo',           type: 'image' },
          { key: 'hero_image',    label: 'Hero Image',     type: 'image' },
          { key: 'order_link',    label: 'Order / Book Link', type: 'url',  placeholder: 'https://...' },
        ],
      },
      {
        id: 'menu',
        label: 'Menu',
        fields: [
          {
            key: 'menu',
            label: 'Menu Items',
            type: 'list',
            variant: 'menu',
            addLabel: 'Add Menu Item',
            subFields: [
              { key: 'category',    label: 'Category',    type: 'text',     placeholder: 'Burgers, Tacos, Drinks...' },
              { key: 'name',        label: 'Item Name',   type: 'text',     placeholder: 'The Good Classic' },
              { key: 'description', label: 'Description', type: 'textarea', placeholder: '...' },
              { key: 'price',       label: 'Price',       type: 'text',     placeholder: '£10.50' },
              { key: 'image',       label: 'Photo',       type: 'image' },
            ],
          },
        ],
      },
      {
        id: 'schedule',
        label: 'Schedule & Location',
        fields: [
          {
            key: 'locations',
            label: 'Weekly Locations',
            type: 'list',
            variant: 'table',
            addLabel: 'Add Location',
            subFields: [
              { key: 'day',      label: 'Day',      type: 'text', placeholder: 'Monday' },
              { key: 'location', label: 'Location', type: 'text', placeholder: 'Borough Market, London' },
              { key: 'time',     label: 'Time',     type: 'text', placeholder: '11am – 3pm' },
            ],
          },
        ],
      },
      { id: 'gallery', label: 'Gallery', fields: [{ key: 'gallery', label: 'Gallery', type: 'multi-image' }] },
      {
        id: 'contact',
        label: 'Contact',
        fields: [
          { key: 'phone',     label: 'Phone',           type: 'text', placeholder: '+44 7700 900000' },
          { key: 'email',     label: 'Email',           type: 'text', placeholder: 'hello@foodtruck.com' },
          { key: 'instagram', label: 'Instagram',       type: 'url',  placeholder: 'https://instagram.com/...' },
          { key: 'facebook',  label: 'Facebook',        type: 'url',  placeholder: 'https://facebook.com/...' },
          { key: 'tiktok',        label: 'TikTok',          type: 'url',  placeholder: 'https://tiktok.com/...' },
          { key: 'whatsapp_link', label: 'WhatsApp Link',   type: 'url',  placeholder: 'https://wa.me/447700900000' },
          { key: 'maps_link',     label: 'Google Maps Link',type: 'url',  placeholder: 'https://maps.google.com/...' },
        ],
      },
      {
        id: 'seo',
        label: 'SEO',
        fields: [
          { key: 'seo_title',       label: 'Page Title',       type: 'text',     placeholder: '' },
          { key: 'seo_description', label: 'Meta Description', type: 'textarea', placeholder: 'Up to 160 characters...' },
        ],
      },
    ],
  },

  retail: {
    label: 'Retail / Shop',
    icon: '🛍️',
    tabs: [
      {
        id: 'business',
        label: 'Business Info',
        fields: [
          { key: 'business_name', label: 'Business Name', type: 'text',     placeholder: 'My Shop' },
          { key: 'tagline',       label: 'Tagline',       type: 'text',     placeholder: 'Quality you can trust' },
          { key: 'about',         label: 'About',         type: 'textarea', placeholder: 'About the shop...' },
          { key: 'logo',          label: 'Logo',          type: 'image' },
          { key: 'hero_image',    label: 'Hero Image',    type: 'image' },
          { key: 'shop_link',     label: 'Online Shop Link', type: 'url',  placeholder: 'https://...' },
        ],
      },
      {
        id: 'products',
        label: 'Products / Services',
        fields: [
          {
            key: 'products',
            label: 'Products',
            type: 'list',
            addLabel: 'Add Product',
            subFields: [
              { key: 'name',        label: 'Product Name', type: 'text',     placeholder: 'Leather Wallet' },
              { key: 'description', label: 'Description',  type: 'textarea', placeholder: '...' },
              { key: 'price',       label: 'Price',        type: 'text',     placeholder: '£24.99' },
              { key: 'image',       label: 'Photo',        type: 'image' },
            ],
          },
          {
            key: 'promotions',
            label: 'Promotions',
            type: 'list',
            addLabel: 'Add Promotion',
            subFields: [
              { key: 'title',       label: 'Title',       type: 'text',     placeholder: '20% off this weekend' },
              { key: 'description', label: 'Description', type: 'textarea', placeholder: '...' },
              { key: 'image',       label: 'Banner',      type: 'image' },
            ],
          },
        ],
      },
      { id: 'hours',   label: 'Hours',   fields: [{ key: 'hours',   label: 'Opening Hours', type: 'hours' }, { key: 'holiday_hours', label: 'Holiday hours note', type: 'text', placeholder: 'e.g. Closed Christmas Day and Jan 1' }] },
      { id: 'gallery', label: 'Gallery', fields: [{ key: 'gallery', label: 'Gallery',        type: 'multi-image' }] },
      {
        id: 'contact',
        label: 'Contact',
        fields: [
          { key: 'phone',     label: 'Phone',           type: 'text',     placeholder: '' },
          { key: 'email',     label: 'Email',           type: 'text',     placeholder: '' },
          { key: 'address',   label: 'Address',         type: 'textarea', placeholder: '' },
          { key: 'instagram', label: 'Instagram',       type: 'url',      placeholder: '' },
          { key: 'facebook',  label: 'Facebook',        type: 'url',      placeholder: '' },
          { key: 'maps_link', label: 'Google Maps Link',type: 'url',      placeholder: '' },
        ],
      },
      {
        id: 'seo',
        label: 'SEO',
        fields: [
          { key: 'seo_title',       label: 'Page Title',       type: 'text',     placeholder: '' },
          { key: 'seo_description', label: 'Meta Description', type: 'textarea', placeholder: 'Up to 160 characters...' },
        ],
      },
    ],
  },

  beauty: {
    label: 'Beauty / Salon / Spa',
    icon: '💅',
    tabs: [
      {
        id: 'business',
        label: 'Business Info',
        fields: [
          { key: 'business_name', label: 'Business Name', type: 'text',     placeholder: 'Luxe Salon' },
          { key: 'tagline',       label: 'Tagline',       type: 'text',     placeholder: 'Look and feel your best' },
          { key: 'about',         label: 'About',         type: 'textarea', placeholder: 'About the salon...' },
          { key: 'logo',          label: 'Logo',          type: 'image' },
          { key: 'hero_image',    label: 'Hero Image',    type: 'image' },
          { key: 'booking_link',  label: 'Booking Link',  type: 'url',      placeholder: 'https://booksy.com/...' },
        ],
      },
      {
        id: 'services',
        label: 'Services',
        fields: [
          {
            key: 'services',
            label: 'Services',
            type: 'list',
            addLabel: 'Add Service',
            subFields: [
              { key: 'name',        label: 'Service Name', type: 'text',     placeholder: 'Haircut & Style' },
              { key: 'description', label: 'Description',  type: 'textarea', placeholder: '...' },
              { key: 'price',       label: 'Price',        type: 'text',     placeholder: 'From £35' },
              { key: 'duration',    label: 'Duration',     type: 'text',     placeholder: '45 min' },
            ],
          },
        ],
      },
      {
        id: 'team',
        label: 'Team',
        fields: [
          {
            key: 'team',
            label: 'Team Members',
            type: 'list',
            addLabel: 'Add Team Member',
            subFields: [
              { key: 'name',        label: 'Name',        type: 'text',     placeholder: 'Sarah Jones' },
              { key: 'role',        label: 'Role',        type: 'text',     placeholder: 'Senior Stylist' },
              { key: 'bio',         label: 'Bio',         type: 'textarea', placeholder: '...' },
              { key: 'photo',       label: 'Photo',       type: 'image' },
            ],
          },
        ],
      },
      { id: 'hours',   label: 'Hours',   fields: [{ key: 'hours',   label: 'Opening Hours', type: 'hours' }, { key: 'holiday_hours', label: 'Holiday hours note', type: 'text', placeholder: 'e.g. Closed Christmas Day and Jan 1' }] },
      { id: 'gallery', label: 'Gallery', fields: [{ key: 'gallery', label: 'Gallery',        type: 'multi-image' }] },
      {
        id: 'contact',
        label: 'Contact',
        fields: [
          { key: 'phone',     label: 'Phone',           type: 'text', placeholder: '' },
          { key: 'email',     label: 'Email',           type: 'text', placeholder: '' },
          { key: 'address',   label: 'Address',         type: 'textarea', placeholder: '' },
          { key: 'instagram', label: 'Instagram',       type: 'url',  placeholder: '' },
          { key: 'facebook',  label: 'Facebook',        type: 'url',  placeholder: '' },
          { key: 'maps_link', label: 'Google Maps Link',type: 'url',  placeholder: '' },
        ],
      },
      {
        id: 'seo',
        label: 'SEO',
        fields: [
          { key: 'seo_title',       label: 'Page Title',       type: 'text',     placeholder: '' },
          { key: 'seo_description', label: 'Meta Description', type: 'textarea', placeholder: 'Up to 160 characters...' },
        ],
      },
    ],
  },

  fitness: {
    label: 'Fitness / Gym',
    icon: '💪',
    tabs: [
      {
        id: 'business',
        label: 'Business Info',
        fields: [
          { key: 'business_name', label: 'Business Name', type: 'text',     placeholder: 'Iron Gym' },
          { key: 'tagline',       label: 'Tagline',       type: 'text',     placeholder: 'Train harder. Live better.' },
          { key: 'about',         label: 'About',         type: 'textarea', placeholder: 'About the gym...' },
          { key: 'logo',          label: 'Logo',          type: 'image' },
          { key: 'hero_image',    label: 'Hero Image',    type: 'image' },
          { key: 'booking_link',  label: 'Booking / Trial Link', type: 'url', placeholder: 'https://...' },
        ],
      },
      {
        id: 'classes',
        label: 'Classes',
        fields: [
          {
            key: 'classes',
            label: 'Class Schedule',
            type: 'list',
            addLabel: 'Add Class',
            subFields: [
              { key: 'name',       label: 'Class Name',  type: 'text', placeholder: 'HIIT' },
              { key: 'day',        label: 'Day',         type: 'text', placeholder: 'Monday' },
              { key: 'time',       label: 'Time',        type: 'text', placeholder: '6:30am' },
              { key: 'instructor', label: 'Instructor',  type: 'text', placeholder: 'Coach Mike' },
              { key: 'duration',   label: 'Duration',    type: 'text', placeholder: '45 min' },
              { key: 'level',      label: 'Level',       type: 'text', placeholder: 'All levels' },
            ],
          },
        ],
      },
      {
        id: 'membership',
        label: 'Membership',
        fields: [
          {
            key: 'plans',
            label: 'Membership Plans',
            type: 'list',
            addLabel: 'Add Plan',
            subFields: [
              { key: 'name',        label: 'Plan Name',   type: 'text',     placeholder: 'Monthly' },
              { key: 'price',       label: 'Price',       type: 'text',     placeholder: '£39/month' },
              { key: 'description', label: 'Description', type: 'textarea', placeholder: 'What is included...' },
            ],
          },
        ],
      },
      {
        id: 'trainers',
        label: 'Trainers',
        fields: [
          {
            key: 'trainers',
            label: 'Trainers',
            type: 'list',
            addLabel: 'Add Trainer',
            subFields: [
              { key: 'name',        label: 'Name',        type: 'text',     placeholder: 'Coach Mike' },
              { key: 'specialty',   label: 'Specialty',   type: 'text',     placeholder: 'Strength & Conditioning' },
              { key: 'bio',         label: 'Bio',         type: 'textarea', placeholder: '...' },
              { key: 'photo',       label: 'Photo',       type: 'image' },
            ],
          },
        ],
      },
      { id: 'hours',   label: 'Hours',   fields: [{ key: 'hours',   label: 'Opening Hours', type: 'hours' }, { key: 'holiday_hours', label: 'Holiday hours note', type: 'text', placeholder: 'e.g. Closed Christmas Day and Jan 1' }] },
      { id: 'gallery', label: 'Gallery', fields: [{ key: 'gallery', label: 'Gallery',        type: 'multi-image' }] },
      {
        id: 'contact',
        label: 'Contact',
        fields: [
          { key: 'phone',     label: 'Phone',           type: 'text', placeholder: '' },
          { key: 'email',     label: 'Email',           type: 'text', placeholder: '' },
          { key: 'address',   label: 'Address',         type: 'textarea', placeholder: '' },
          { key: 'instagram', label: 'Instagram',       type: 'url',  placeholder: '' },
          { key: 'facebook',  label: 'Facebook',        type: 'url',  placeholder: '' },
          { key: 'maps_link', label: 'Google Maps Link',type: 'url',  placeholder: '' },
        ],
      },
      {
        id: 'seo',
        label: 'SEO',
        fields: [
          { key: 'seo_title',       label: 'Page Title',       type: 'text',     placeholder: '' },
          { key: 'seo_description', label: 'Meta Description', type: 'textarea', placeholder: 'Up to 160 characters...' },
        ],
      },
    ],
  },

  professional_services: {
    label: 'Professional Services',
    icon: '💼',
    tabs: [
      {
        id: 'business',
        label: 'Business Info',
        fields: [
          { key: 'business_name', label: 'Business Name', type: 'text',     placeholder: 'Smith & Associates' },
          { key: 'tagline',       label: 'Tagline',       type: 'text',     placeholder: 'Expert advice you can trust' },
          { key: 'about',         label: 'About',         type: 'textarea', placeholder: 'About the firm...' },
          { key: 'logo',          label: 'Logo',          type: 'image' },
          { key: 'hero_image',    label: 'Hero Image',    type: 'image' },
        ],
      },
      {
        id: 'services',
        label: 'Services',
        fields: [
          {
            key: 'services',
            label: 'Services',
            type: 'list',
            addLabel: 'Add Service',
            subFields: [
              { key: 'name',        label: 'Service',     type: 'text',     placeholder: 'Corporate Law' },
              { key: 'description', label: 'Description', type: 'textarea', placeholder: '...' },
            ],
          },
        ],
      },
      {
        id: 'team',
        label: 'Team',
        fields: [
          {
            key: 'team',
            label: 'Team Members',
            type: 'list',
            addLabel: 'Add Team Member',
            subFields: [
              { key: 'name',           label: 'Name',           type: 'text',     placeholder: 'John Smith' },
              { key: 'title',          label: 'Title',          type: 'text',     placeholder: 'Senior Partner' },
              { key: 'bio',            label: 'Bio',            type: 'textarea', placeholder: '...' },
              { key: 'credentials',    label: 'Credentials',    type: 'text',     placeholder: 'LLB, MBA' },
              { key: 'photo',          label: 'Photo',          type: 'image' },
            ],
          },
        ],
      },
      {
        id: 'testimonials',
        label: 'Testimonials',
        fields: [
          {
            key: 'testimonials',
            label: 'Client Testimonials',
            type: 'list',
            addLabel: 'Add Testimonial',
            subFields: [
              { key: 'name',    label: 'Client Name', type: 'text',     placeholder: 'Jane D.' },
              { key: 'quote',   label: 'Quote',       type: 'textarea', placeholder: "Excellent service..." },
              { key: 'company', label: 'Company',     type: 'text',     placeholder: 'ABC Ltd (optional)' },
            ],
          },
        ],
      },
      { id: 'gallery', label: 'Gallery', fields: [{ key: 'gallery', label: 'Gallery', type: 'multi-image' }] },
      {
        id: 'contact',
        label: 'Contact',
        fields: [
          { key: 'phone',     label: 'Phone',           type: 'text',     placeholder: '' },
          { key: 'email',     label: 'Email',           type: 'text',     placeholder: '' },
          { key: 'address',   label: 'Address',         type: 'textarea', placeholder: '' },
          { key: 'linkedin',  label: 'LinkedIn',        type: 'url',      placeholder: '' },
          { key: 'maps_link', label: 'Google Maps Link',type: 'url',      placeholder: '' },
        ],
      },
      {
        id: 'seo',
        label: 'SEO',
        fields: [
          { key: 'seo_title',       label: 'Page Title',       type: 'text',     placeholder: '' },
          { key: 'seo_description', label: 'Meta Description', type: 'textarea', placeholder: 'Up to 160 characters...' },
        ],
      },
    ],
  },

  healthcare: {
    label: 'Healthcare',
    icon: '🏥',
    tabs: [
      {
        id: 'business',
        label: 'Business Info',
        fields: [
          { key: 'business_name', label: 'Practice Name', type: 'text',     placeholder: 'City Dental' },
          { key: 'tagline',       label: 'Tagline',       type: 'text',     placeholder: 'Your health is our priority' },
          { key: 'about',         label: 'About',         type: 'textarea', placeholder: 'About the practice...' },
          { key: 'logo',          label: 'Logo',          type: 'image' },
          { key: 'hero_image',    label: 'Hero Image',    type: 'image' },
          { key: 'booking_link',  label: 'Booking Link',  type: 'url',      placeholder: 'https://...' },
        ],
      },
      {
        id: 'services',
        label: 'Services',
        fields: [
          {
            key: 'services',
            label: 'Services',
            type: 'list',
            addLabel: 'Add Service',
            subFields: [
              { key: 'name',        label: 'Service',     type: 'text',     placeholder: 'General Check-up' },
              { key: 'description', label: 'Description', type: 'textarea', placeholder: '...' },
              { key: 'price',       label: 'Price',       type: 'text',     placeholder: 'From £60 (optional)' },
            ],
          },
        ],
      },
      {
        id: 'team',
        label: 'Team',
        fields: [
          {
            key: 'team',
            label: 'Practitioners',
            type: 'list',
            addLabel: 'Add Practitioner',
            subFields: [
              { key: 'name',        label: 'Name',          type: 'text',     placeholder: 'Dr. Sarah Jones' },
              { key: 'title',       label: 'Title',         type: 'text',     placeholder: 'Principal Dentist' },
              { key: 'bio',         label: 'Bio',           type: 'textarea', placeholder: '...' },
              { key: 'credentials', label: 'Credentials',   type: 'text',     placeholder: 'BDS, MFDS RCS' },
              { key: 'photo',       label: 'Photo',         type: 'image' },
            ],
          },
        ],
      },
      {
        id: 'insurance',
        label: 'Insurance',
        fields: [
          {
            key: 'insurance',
            label: 'Insurance / Funds Accepted',
            type: 'list',
            addLabel: 'Add Insurance',
            subFields: [
              { key: 'name', label: 'Provider', type: 'text', placeholder: 'BUPA, AXA, NHS...' },
            ],
          },
        ],
      },
      { id: 'hours',   label: 'Hours',   fields: [{ key: 'hours',   label: 'Opening Hours', type: 'hours' }, { key: 'holiday_hours', label: 'Holiday hours note', type: 'text', placeholder: 'e.g. Closed Christmas Day and Jan 1' }] },
      { id: 'gallery', label: 'Gallery', fields: [{ key: 'gallery', label: 'Gallery',        type: 'multi-image' }] },
      {
        id: 'contact',
        label: 'Contact',
        fields: [
          { key: 'phone',     label: 'Phone',           type: 'text',     placeholder: '' },
          { key: 'email',     label: 'Email',           type: 'text',     placeholder: '' },
          { key: 'address',   label: 'Address',         type: 'textarea', placeholder: '' },
          { key: 'maps_link', label: 'Google Maps Link',type: 'url',      placeholder: '' },
        ],
      },
      {
        id: 'seo',
        label: 'SEO',
        fields: [
          { key: 'seo_title',       label: 'Page Title',       type: 'text',     placeholder: '' },
          { key: 'seo_description', label: 'Meta Description', type: 'textarea', placeholder: 'Up to 160 characters...' },
        ],
      },
    ],
  },

  photography: {
    label: 'Photography / Creative',
    icon: '📷',
    tabs: [
      {
        id: 'business',
        label: 'Business Info',
        fields: [
          { key: 'business_name', label: 'Business Name', type: 'text',     placeholder: 'Lens & Light Photography' },
          { key: 'tagline',       label: 'Tagline',       type: 'text',     placeholder: 'Capturing your story' },
          { key: 'about',         label: 'About',         type: 'textarea', placeholder: 'About me / us...' },
          { key: 'logo',          label: 'Logo',          type: 'image' },
          { key: 'hero_image',    label: 'Hero Image',    type: 'image' },
          { key: 'booking_link',  label: 'Booking / Inquiry Link', type: 'url', placeholder: 'https://...' },
        ],
      },
      {
        id: 'portfolio',
        label: 'Portfolio',
        fields: [
          {
            key: 'portfolio',
            label: 'Portfolio Categories',
            type: 'list',
            addLabel: 'Add Category',
            subFields: [
              { key: 'category', label: 'Category Name', type: 'text',        placeholder: 'Weddings' },
              { key: 'images',   label: 'Images',        type: 'multi-image' },
            ],
          },
        ],
      },
      {
        id: 'packages',
        label: 'Packages',
        fields: [
          {
            key: 'packages',
            label: 'Packages / Pricing',
            type: 'list',
            addLabel: 'Add Package',
            subFields: [
              { key: 'name',        label: 'Package Name', type: 'text',     placeholder: 'Wedding Full Day' },
              { key: 'price',       label: 'Price',        type: 'text',     placeholder: 'From £1,200' },
              { key: 'description', label: 'Includes',     type: 'textarea', placeholder: '8 hours, 500 edited photos...' },
            ],
          },
        ],
      },
      { id: 'gallery', label: 'Gallery', fields: [{ key: 'gallery', label: 'Gallery', type: 'multi-image' }] },
      {
        id: 'contact',
        label: 'Contact',
        fields: [
          { key: 'phone',     label: 'Phone',     type: 'text', placeholder: '' },
          { key: 'email',     label: 'Email',     type: 'text', placeholder: '' },
          { key: 'instagram', label: 'Instagram', type: 'url',  placeholder: '' },
          { key: 'facebook',  label: 'Facebook',  type: 'url',  placeholder: '' },
        ],
      },
      {
        id: 'seo',
        label: 'SEO',
        fields: [
          { key: 'seo_title',       label: 'Page Title',       type: 'text',     placeholder: '' },
          { key: 'seo_description', label: 'Meta Description', type: 'textarea', placeholder: 'Up to 160 characters...' },
        ],
      },
    ],
  },

  events: {
    label: 'Events / Catering / Venue',
    icon: '🎉',
    tabs: [
      {
        id: 'business',
        label: 'Business Info',
        fields: [
          { key: 'business_name', label: 'Business Name', type: 'text',     placeholder: 'Grand Events Co.' },
          { key: 'tagline',       label: 'Tagline',       type: 'text',     placeholder: 'Making every moment memorable' },
          { key: 'about',         label: 'About',         type: 'textarea', placeholder: 'About us...' },
          { key: 'logo',          label: 'Logo',          type: 'image' },
          { key: 'hero_image',    label: 'Hero Image',    type: 'image' },
          { key: 'capacity',      label: 'Capacity / Location Details', type: 'text', placeholder: 'Up to 200 guests, Central London' },
          { key: 'inquiry_link',  label: 'Inquiry / Booking Link', type: 'url', placeholder: 'https://...' },
        ],
      },
      {
        id: 'services',
        label: 'Services',
        fields: [
          {
            key: 'services',
            label: 'Services Offered',
            type: 'list',
            addLabel: 'Add Service',
            subFields: [
              { key: 'name',        label: 'Service',     type: 'text',     placeholder: 'Corporate Catering' },
              { key: 'description', label: 'Description', type: 'textarea', placeholder: '...' },
            ],
          },
        ],
      },
      {
        id: 'packages',
        label: 'Packages',
        fields: [
          {
            key: 'packages',
            label: 'Packages',
            type: 'list',
            addLabel: 'Add Package',
            subFields: [
              { key: 'name',        label: 'Package Name', type: 'text',     placeholder: 'Silver Package' },
              { key: 'price',       label: 'Price',        type: 'text',     placeholder: 'From £2,500' },
              { key: 'description', label: 'Includes',     type: 'textarea', placeholder: '...' },
            ],
          },
        ],
      },
      { id: 'gallery', label: 'Gallery', fields: [{ key: 'gallery', label: 'Gallery', type: 'multi-image' }] },
      {
        id: 'contact',
        label: 'Contact',
        fields: [
          { key: 'phone',     label: 'Phone',           type: 'text',     placeholder: '' },
          { key: 'email',     label: 'Email',           type: 'text',     placeholder: '' },
          { key: 'address',   label: 'Address',         type: 'textarea', placeholder: '' },
          { key: 'instagram', label: 'Instagram',       type: 'url',      placeholder: '' },
          { key: 'facebook',  label: 'Facebook',        type: 'url',      placeholder: '' },
          { key: 'maps_link', label: 'Google Maps Link',type: 'url',      placeholder: '' },
        ],
      },
      {
        id: 'seo',
        label: 'SEO',
        fields: [
          { key: 'seo_title',       label: 'Page Title',       type: 'text',     placeholder: '' },
          { key: 'seo_description', label: 'Meta Description', type: 'textarea', placeholder: 'Up to 160 characters...' },
        ],
      },
    ],
  },

  general: {
    label: 'General Business',
    icon: '🏢',
    tabs: [
      {
        id: 'business',
        label: 'Business Info',
        fields: [
          { key: 'business_name', label: 'Business Name', type: 'text',     placeholder: 'My Business' },
          { key: 'tagline',       label: 'Tagline',       type: 'text',     placeholder: '' },
          { key: 'about',         label: 'About',         type: 'textarea', placeholder: 'About us...' },
          { key: 'logo',          label: 'Logo',          type: 'image' },
          { key: 'hero_image',    label: 'Hero Image',    type: 'image' },
        ],
      },
      {
        id: 'services',
        label: 'Services',
        fields: [
          {
            key: 'services',
            label: 'Services',
            type: 'list',
            addLabel: 'Add Service',
            subFields: [
              { key: 'name',        label: 'Service',     type: 'text',     placeholder: '' },
              { key: 'description', label: 'Description', type: 'textarea', placeholder: '' },
              { key: 'price',       label: 'Price',       type: 'text',     placeholder: '' },
            ],
          },
        ],
      },
      { id: 'hours',   label: 'Hours',   fields: [{ key: 'hours',   label: 'Opening Hours', type: 'hours' }, { key: 'holiday_hours', label: 'Holiday hours note', type: 'text', placeholder: 'e.g. Closed Christmas Day and Jan 1' }] },
      { id: 'gallery', label: 'Gallery', fields: [{ key: 'gallery', label: 'Gallery',        type: 'multi-image' }] },
      {
        id: 'contact',
        label: 'Contact',
        fields: [
          { key: 'phone',     label: 'Phone',           type: 'text',     placeholder: '' },
          { key: 'email',     label: 'Email',           type: 'text',     placeholder: '' },
          { key: 'address',   label: 'Address',         type: 'textarea', placeholder: '' },
          { key: 'instagram', label: 'Instagram',       type: 'url',      placeholder: '' },
          { key: 'facebook',  label: 'Facebook',        type: 'url',      placeholder: '' },
          { key: 'maps_link', label: 'Google Maps Link',type: 'url',      placeholder: '' },
        ],
      },
      {
        id: 'seo',
        label: 'SEO',
        fields: [
          { key: 'seo_title',       label: 'Page Title',       type: 'text',     placeholder: '' },
          { key: 'seo_description', label: 'Meta Description', type: 'textarea', placeholder: 'Up to 160 characters...' },
        ],
      },
    ],
  },
}

// Flat list for dropdowns
export const INDUSTRY_OPTIONS = Object.entries(INDUSTRIES).map(([value, schema]) => ({
  value,
  label: schema.label,
  icon: schema.icon,
}))

// Default empty hours structure
export const DEFAULT_HOURS = [
  { day: 'Monday',    open: true,  openTime: '09:00', closeTime: '17:00' },
  { day: 'Tuesday',   open: true,  openTime: '09:00', closeTime: '17:00' },
  { day: 'Wednesday', open: true,  openTime: '09:00', closeTime: '17:00' },
  { day: 'Thursday',  open: true,  openTime: '09:00', closeTime: '17:00' },
  { day: 'Friday',    open: true,  openTime: '09:00', closeTime: '17:00' },
  { day: 'Saturday',  open: false, openTime: '10:00', closeTime: '16:00' },
  { day: 'Sunday',    open: false, openTime: '10:00', closeTime: '16:00' },
]
